import { useEffect, useRef, useState } from 'react';

import { Card, IconButton, NumberField, PropRow, SelectField, TextField } from '../ui/controls.js';
import { EffectsEditor } from './EffectsEditor.js';
import { NotesTable } from './NotesTable.js';
import { docVariables, ParamField } from './ParamField.js';
import { withField } from './SchemaFields.js';
import { isNum, isObj } from './param-values.js';
import { IPA_GROUPS, SINGER_VOICES } from './sound-catalog.js';
import { formatBytes, previewDoc, previewDocument, stopPreview, useSoundCache, type CacheEntry } from './sound-api.js';
import './sound-editors.css';

type Obj = Record<string, unknown>;

/** Multi-line text committed on blur. */
export function TextArea({ value, onChange, placeholder, rows = 2 }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <textarea
      className="snd-textarea"
      rows={rows}
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== value) onChange(text);
      }}
    />
  );
}

/** Splits lyrics into syllables: words by whitespace, syllables by "-" (`"ma-ry had a lit-tle"`). */
export function lyricSyllables(text: string): string[] {
  return text
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((w) => {
      const parts = w.split('-').filter(Boolean);
      return parts.map((p, i) => (i < parts.length - 1 ? `${p}-` : p));
    });
}

/** Assigns syllables to notes in order, appending notes (1 beat each, same pitch) when there are more syllables. */
export function assignLyrics(notes: Obj[], syllables: string[]): Obj[] {
  const out = notes.map((n) => ({ ...n }));
  syllables.forEach((s, i) => {
    let n = out[i];
    if (!n) {
      const prev = out[i - 1];
      const t = prev && isNum(prev.t) ? prev.t + (isNum(prev.d) ? prev.d : 1) : 0;
      n = { t, d: 1, p: prev?.p ?? 'C4', v: 0.8 };
      out.push(n);
    }
    n.lyric = s;
  });
  return out;
}

/* ------------------------------------------------------------------ */
/* Voice picker                                                        */

/**
 * Piper voice id picker: installed voices first, then downloadable ones from
 * the shared cache catalogue (`GET /api/sound/cache`), with a download
 * button for a chosen voice that is not installed.
 */
export function VoicePicker({ value, onChange }: { value: string | undefined; onChange: (id: string | undefined) => void }) {
  const entries = useSoundCache((s) => s.entries);
  const load = useSoundCache((s) => s.load);
  const downloads = useSoundCache((s) => s.downloads);
  const download = useSoundCache((s) => s.download);
  const error = useSoundCache((s) => s.error);
  useEffect(() => void load(), [load]);

  const voices = entries.filter((e) => e.kind === 'voice');
  const installed = voices.filter((v) => v.installed);
  const available = voices.filter((v) => !v.installed);
  const current = voices.find((v) => v.id === value);
  const dl = value ? downloads[`voice:${value}`] : undefined;
  const opt = (v: CacheEntry, inst: boolean) => ({
    value: v.id,
    label: inst ? `✓ ${v.id}` : `↓ ${v.id}${v.size ? ` (${formatBytes(v.size)})` : ''}`,
  });

  return (
    <div className="snd-voicepick">
      <div className="snd-row">
        <SelectField
          value={value}
          placeholder="Choose a voice…"
          options={[...installed.map((v) => opt(v, true)), ...available.map((v) => opt(v, false))]}
          onChange={(v) => onChange(v || undefined)}
        />
        <IconButton icon="refresh" title="Refresh voice list" size={13} onClick={() => void load(true)} />
      </div>
      {value && current && !current.installed && (
        <div className="snd-row">
          {dl && !dl.error ? (
            <Progress value={dl.progress} label="Downloading…" />
          ) : (
            <button type="button" className="snd-btn" onClick={() => void download('voice', value)}>
              {`Download ${value}${current.size ? ` (${formatBytes(current.size)})` : ''}`}
            </button>
          )}
          {current.license && <span className="snd-hint">{current.license}</span>}
        </div>
      )}
      {value && !current && voices.length > 0 && <span className="snd-hint is-warn">Not in the catalogue — falls back to the built-in formant voice.</span>}
      {dl?.error && <span className="snd-hint is-error">{dl.error}</span>}
      {error && voices.length === 0 && <span className="snd-hint">Voice list unavailable: type an id (lang_REGION-name-quality).</span>}
      {voices.length === 0 && <TextField value={value} mono placeholder="en_US-amy-medium" onChange={(t) => onChange(t.trim() || undefined)} />}
    </div>
  );
}

export function Progress({ value, label }: { value: number | null; label?: string }) {
  return (
    <div className={`snd-progress${value === null ? ' is-indeterminate' : ''}`} title={label}>
      <div className="snd-progress-bar" style={value === null ? undefined : { width: `${Math.round(value * 100)}%` }} />
      <span className="snd-progress-text">{value === null ? label ?? '…' : `${Math.round(value * 100)}%`}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phonemes                                                            */

function PhonemeField({ value, onChange }: { value: string | undefined; onChange: (v: string | undefined) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value ?? '');
  const [helper, setHelper] = useState(false);
  useEffect(() => setText(value ?? ''), [value]);
  const commit = (t: string) => {
    if (t !== (value ?? '')) onChange(t.trim() === '' ? undefined : t);
  };
  const insert = (sym: string) => {
    const el = input.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + sym + text.slice(end);
    setText(next);
    commit(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + sym.length, start + sym.length);
    });
  };
  return (
    <div className="snd-phonemes">
      <div className="snd-row">
        <input
          ref={input}
          className="textfield mono"
          value={text}
          placeholder="ðə ɡˈeɪt ɪz ˈoʊpən (empty = automatic)"
          onChange={(e) => setText(e.target.value)}
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <IconButton icon="edit" title="IPA helper" size={13} active={helper} onClick={() => setHelper((v) => !v)} />
      </div>
      {helper && (
        <div className="snd-ipa">
          {IPA_GROUPS.map((g) => (
            <div key={g.label} className="snd-ipa-group">
              <span className="snd-mini-label">{g.label}</span>
              <div className="snd-chips">
                {g.symbols.map(([sym, example]) => (
                  <button
                    key={sym}
                    type="button"
                    className="snd-chip is-button mono"
                    title={example}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insert(sym)}
                  >
                    {sym === ' ' ? '␣' : sym}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <span className="snd-hint">espeak-ng IPA as used by Piper voices; stress marks go before the stressed syllable.</span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lines                                                               */

function LineCard({
  line,
  index,
  count,
  onChange,
  onRemove,
  onMove,
  onPreview,
  previewing,
}: {
  line: Obj;
  index: number;
  count: number;
  onChange: (l: Obj) => void;
  onRemove: () => void;
  onMove: (to: number) => void;
  onPreview: () => void;
  previewing: boolean;
}) {
  const set = (k: string, v: unknown) => onChange(withField(line, k, v));
  const num = (k: string) => (isNum(line[k]) ? (line[k] as number) : undefined);
  return (
    <div className="snd-subcard">
      <div className="snd-row">
        <span className="snd-mini-label">{`Line ${index + 1}`}</span>
        <span className="snd-spacer" />
        <IconButton icon={previewing ? 'stop' : 'play'} title={previewing ? 'Stop' : 'Preview this line'} size={13} onClick={onPreview} />
        <IconButton icon="chevronUp" title="Move up" size={12} disabled={index === 0} onClick={() => onMove(index - 1)} />
        <IconButton icon="chevronDown" title="Move down" size={12} disabled={index >= count - 1} onClick={() => onMove(index + 1)} />
        <IconButton icon="trash" title="Remove line" size={13} onClick={onRemove} />
      </div>
      <TextArea value={typeof line.text === 'string' ? line.text : ''} placeholder="What is said…" onChange={(t) => set('text', t)} />
      <div className="snd-grid4">
        <NumberField label="Start" title="Start time (s); omit = after the previous line" value={num('start')} step={0.05} min={0} onChange={(v) => set('start', v)} />
        <NumberField label="Speed" title="Length scale (1 = normal, <1 faster)" value={num('speed')} step={0.05} min={0.1} onChange={(v) => set('speed', v)} />
        <NumberField label="Pitch" title="Semitones (formant-preserving)" value={num('pitch')} step={0.5} onChange={(v) => set('pitch', v)} />
        <NumberField label="Energy" value={num('energy')} step={0.05} min={0} onChange={(v) => set('energy', v)} />
        <NumberField label="Noise" title="VITS noise scale (variation)" value={num('noise')} step={0.05} min={0} onChange={(v) => set('noise', v)} />
        <NumberField label="Pause" title="Silence after the line (s)" value={num('pause')} step={0.05} min={0} onChange={(v) => set('pause', v)} />
        <NumberField label="Spkr" title="Speaker id (multi-speaker voices)" value={num('speaker')} integer min={0} onChange={(v) => set('speaker', v)} />
      </div>
      <PropRow label="Emphasis" hint="Words to stress, comma-separated">
        <TextField
          value={Array.isArray(line.emphasis) ? line.emphasis.join(', ') : ''}
          placeholder="open, now"
          onChange={(t) => {
            const words = t.split(',').map((w) => w.trim()).filter(Boolean);
            set('emphasis', words.length ? words : undefined);
          }}
        />
      </PropRow>
      <PropRow label="Phonemes" hint="Explicit IPA phonemes to fix pronunciation (overrides the text)">
        <PhonemeField value={typeof line.phonemes === 'string' ? line.phonemes : undefined} onChange={(v) => set('phonemes', v)} />
      </PropRow>
    </div>
  );
}

/**
 * Voice editor for a document's `voice` object: speech lines (text, voice,
 * speed/pitch/energy/noise/pause, phoneme override with an IPA helper,
 * per-line preview) or sing mode (singer preset, vibrato/breath/tension,
 * notes with a lyric per note), plus the voice effect chain.
 */
export function VoiceEditor({ value, onChange, doc }: { value: unknown; onChange: (value: unknown) => void; doc?: unknown }) {
  const voice: Obj = isObj(value) ? value : { mode: 'speech', lines: [] };
  const mode = voice.mode === 'sing' ? 'sing' : 'speech';
  const vars = docVariables(doc);
  const set = (k: string, v: unknown) => onChange(withField(voice, k, v));
  const [playing, setPlaying] = useState<number | 'all' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lyrics, setLyrics] = useState('');

  const preview = async (which: number | 'all') => {
    if (playing === which) {
      stopPreview();
      setPlaying(null);
      return;
    }
    setError(null);
    setPlaying(which);
    try {
      const lines = Array.isArray(voice.lines) ? voice.lines : [];
      const v = which === 'all' ? voice : { ...voice, lines: [{ ...(isObj(lines[which]) ? lines[which] : {}), start: 0 }] };
      await previewDoc(previewDocument('Voice preview', { kind: 'voice', voice: v }, doc));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPlaying(null);
    }
  };

  const lines: Obj[] = Array.isArray(voice.lines) ? voice.lines.map((l) => (isObj(l) ? l : {})) : [];
  const notes: Obj[] = Array.isArray(voice.notes) ? voice.notes.map((n) => (isObj(n) ? n : {})) : [];
  const setLines = (next: Obj[]) => set('lines', next);

  return (
    <div className="snd-editor">
      <PropRow label="Mode">
        <SelectField
          value={mode}
          options={[
            { value: 'speech', label: 'Speech (neural TTS)' },
            { value: 'sing', label: 'Sing (synthesised singer)' },
          ]}
          onChange={(m) => set('mode', m)}
        />
      </PropRow>

      {mode === 'speech' ? (
        <>
          <PropRow label="Voice" hint="Piper voice id (<lang>_<REGION>-<name>-<quality>)">
            <VoicePicker value={typeof voice.voice === 'string' ? voice.voice : undefined} onChange={(v) => set('voice', v)} />
          </PropRow>
          <Card
            title="Lines"
            subtitle={`${lines.length}`}
            actions={
              <>
                <IconButton icon={playing === 'all' ? 'stop' : 'play'} title="Preview all lines" size={13} onClick={() => void preview('all')} />
                <IconButton icon="plus" title="Add line" size={13} onClick={() => setLines([...lines, { text: '', pause: 0.3 }])} />
              </>
            }
          >
            {lines.length === 0 && <span className="snd-hint">No lines yet.</span>}
            {lines.map((l, i) => (
              <LineCard
                key={i}
                line={l}
                index={i}
                count={lines.length}
                previewing={playing === i}
                onChange={(nl) => setLines(lines.map((x, j) => (j === i ? nl : x)))}
                onRemove={() => setLines(lines.filter((_, j) => j !== i))}
                onMove={(to) => {
                  const next = [...lines];
                  const [m] = next.splice(i, 1);
                  if (m) next.splice(to, 0, m);
                  setLines(next);
                }}
                onPreview={() => void preview(i)}
              />
            ))}
          </Card>
        </>
      ) : (
        <>
          <PropRow label="Singer">
            <SelectField value={typeof voice.voice === 'string' ? voice.voice : undefined} placeholder="soprano" options={SINGER_VOICES} onChange={(v) => set('voice', v)} />
          </PropRow>
          <PropRow label="Vibrato" hint="Vibrato depth (semitones); accepts envelopes/LFOs">
            <ParamField value={voice.vibrato} step={0.05} min={0} placeholder={0.3} vars={vars} onChange={(v) => set('vibrato', v)} />
          </PropRow>
          <PropRow label="Breath" hint="Breathiness 0..1">
            <ParamField value={voice.breath} step={0.05} min={0} max={1} placeholder={0.2} vars={vars} onChange={(v) => set('breath', v)} />
          </PropRow>
          <PropRow label="Tension" hint="Vocal tension 0..1 (soft .. pressed)">
            <ParamField value={voice.tension} step={0.05} min={0} max={1} placeholder={0.5} vars={vars} onChange={(v) => set('tension', v)} />
          </PropRow>
          <Card
            title="Notes & lyrics"
            subtitle={`${notes.length}`}
            actions={<IconButton icon={playing === 'all' ? 'stop' : 'play'} title="Preview" size={13} onClick={() => void preview('all')} />}
          >
            <NotesTable notes={notes} lyric onChange={(n) => set('notes', n)} />
            <div className="snd-lyrics">
              <TextArea value={lyrics} rows={2} placeholder="Lyrics: words by spaces, syllables by hyphens (twin-kle twin-kle lit-tle star)" onChange={setLyrics} />
              <button type="button" className="snd-btn" disabled={!lyrics.trim()} onClick={() => set('notes', assignLyrics(notes, lyricSyllables(lyrics)))}>
                Apply lyrics to notes
              </button>
            </div>
          </Card>
        </>
      )}

      <Card title="Voice effects" defaultOpen={Array.isArray(voice.effects) && voice.effects.length > 0}>
        <EffectsEditor value={voice.effects} doc={doc} onChange={(v) => set('effects', v)} />
      </Card>
      {error && <span className="snd-hint is-error">{error}</span>}
    </div>
  );
}
